import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { portalApi } from '../services/pspApi';
import { Alert } from '../components/Feedback';
import { getPspStatusInfo } from '../utils/statusHelper';
import moment from 'moment';

const JobList = () => {
    const { friendly_url } = useParams();
    const navigate = useNavigate();
    const base = `/processos-seletivos/${friendly_url}`;

    const [activeTab, setActiveTab] = useState('local'); // 'local' | 'cargo'
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [processes, setProcesses] = useState([]);
    const [vacancies, setVacancies] = useState([]);
    const [filterOptions, setFilterOptions] = useState({ roles: [], areas: [], locations: [] });

    // Filters for "Vagas por Cargo e Área"
    const [filters, setFilters] = useState({
        search: '',
        role_id: '',
        area_id: '',
        project_id: '',
        process_id: ''
    });

    // Modal de Locais Vinculados
    const [locationsModal, setLocationsModal] = useState({
        open: false,
        title: '',
        projects: []
    });

    useEffect(() => {
        const loadInitialData = async () => {
            setLoading(true);
            setError('');
            try {
                const [procRes, vacRes, filtersRes] = await Promise.all([
                    portalApi.getProcesses(),
                    portalApi.getVacancies(),
                    portalApi.getFilters()
                ]);

                if (procRes && procRes.status === 1) {
                    setProcesses(procRes.processes || []);
                }
                if (vacRes && vacRes.status === 1) {
                    setVacancies(vacRes.vacancies || []);
                }
                if (filtersRes && filtersRes.status === 1) {
                    setFilterOptions({
                        roles: filtersRes.roles || [],
                        areas: filtersRes.areas || [],
                        locations: filtersRes.locations || []
                    });
                }
            } catch (err) {
                console.error('Erro ao carregar dados do portal:', err);
                setError('Não foi possível carregar as vagas no momento. Tente novamente mais tarde.');
            } finally {
                setLoading(false);
            }
        };

        loadInitialData();
    }, []);

    // Filter processes for Tab 1 (Vagas por Local)
    const filteredProcesses = useMemo(() => {
        if (!searchTerm.trim()) return processes;
        const term = searchTerm.toLowerCase().trim();
        return processes.filter(p => {
            const titleMatch = (p.title || '').toLowerCase().includes(term);
            const editalMatch = (p.edital_number || '').toLowerCase().includes(term);
            const unitsMatch = (p.units_and_staffing || '').toLowerCase().includes(term);
            const projectMatch = (p.projects || []).some(proj => (proj.name || '').toLowerCase().includes(term));
            return titleMatch || editalMatch || unitsMatch || projectMatch;
        });
    }, [processes, searchTerm]);

    // Filter vacancies for Tab 2 (Vagas por Cargo e Área)
    const filteredVacancies = useMemo(() => {
        return vacancies.filter(v => {
            // Free text search (from tab top search or filter search)
            const query = (filters.search || searchTerm).toLowerCase().trim();
            if (query) {
                const titleMatch = (v.title || '').toLowerCase().includes(query);
                const roleMatch = (v.role_name || '').toLowerCase().includes(query);
                const areaMatch = (v.area_role_name || '').toLowerCase().includes(query);
                const procMatch = (v.selection_process_title || '').toLowerCase().includes(query);
                const projMatch = (v.projects || []).some(p => (p.name || '').toLowerCase().includes(query));
                if (!titleMatch && !roleMatch && !areaMatch && !procMatch && !projMatch) {
                    return false;
                }
            }

            // Process filter (ao clicar no card de um processo na aba de local)
            if (filters.process_id) {
                const pId = v.selection_process_id || v.psp_selection_process_id;
                if (!pId || String(pId) !== String(filters.process_id)) {
                    return false;
                }
            }

            // Role filter
            if (filters.role_id && String(v.psp_role_id) !== String(filters.role_id)) {
                return false;
            }

            // Area filter
            if (filters.area_id && String(v.area_role_id) !== String(filters.area_id)) {
                return false;
            }

            // Location/Project filter
            if (filters.project_id) {
                const hasProject = (v.projects || []).some(p => String(p.id) === String(filters.project_id));
                if (!hasProject) return false;
            }

            return true;
        });
    }, [vacancies, filters, searchTerm]);

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
    };

    const handleClearFilters = () => {
        setFilters({ search: '', role_id: '', area_id: '', project_id: '', process_id: '' });
        setSearchTerm('');
    };

    const hasActiveFilters = filters.search || filters.role_id || filters.area_id || filters.project_id || filters.process_id || searchTerm;

    // Helper for process status
    const getProcessStatusInfo = (process) => {
        return getPspStatusInfo(process);
    };

    // When clicking a process card: switch to vacancies tab specifically filtered by this process
    const handleProcessCardClick = (process) => {
        setFilters(prev => ({
            ...prev,
            process_id: process.id,
            project_id: '' // Limpa o project_id para que apareçam todas as vagas do processo
        }));
        setSearchTerm('');
        setActiveTab('cargo');
    };

    const openLocationsModal = (title, projects = []) => {
        setLocationsModal({
            open: true,
            title,
            projects
        });
    };

    const closeLocationsModal = () => {
        setLocationsModal({
            open: false,
            title: '',
            projects: []
        });
    };

    const selectedProcessTitle = useMemo(() => {
        if (!filters.process_id) return '';
        const foundProc = processes.find(p => String(p.id) === String(filters.process_id));
        if (foundProc && foundProc.title) return foundProc.title;
        const foundVac = vacancies.find(v => String(v.selection_process_id || v.psp_selection_process_id) === String(filters.process_id));
        if (foundVac && foundVac.selection_process_title) return foundVac.selection_process_title;
        return 'Processo Selecionado';
    }, [filters.process_id, processes, vacancies]);

    return (
        <div className="psp-portal-main psp-fade-in">
            {error && <Alert type="error">{error}</Alert>}

            {/* BARRA DE NAVEGAÇÃO E PESQUISA (Tabs) */}
            <div className="psp-nav-tabs-wrapper">
                <div className="psp-tabs-group">
                    <button
                        type="button"
                        id="tab-local"
                        className={`psp-tab-button ${activeTab === 'local' ? 'is-active' : ''}`}
                        onClick={() => setActiveTab('local')}
                    >
                        Vagas por Local
                    </button>
                    <button
                        type="button"
                        id="tab-cargo"
                        className={`psp-tab-button ${activeTab === 'cargo' ? 'is-active' : ''}`}
                        onClick={() => setActiveTab('cargo')}
                    >
                        Vagas por Cargo e Area de Atuação
                    </button>
                </div>

                <div className="psp-search-box">
                    <i className="fas fa-search psp-search-icon" aria-hidden="true" />
                    <input
                        type="text"
                        placeholder="Pesquisa"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="psp-search-input"
                    />
                </div>
            </div>

            {loading ? (
                <div className="psp-loading">
                    <i className="fas fa-spinner fa-spin fa-2x" />
                    <p>Carregando processos seletivos e vagas...</p>
                </div>
            ) : (
                <>
                    {/* VIEW 1: VAGAS POR LOCAL (GRID) */}
                    {activeTab === 'local' && (
                        <div className="psp-fade-in">
                            {filteredProcesses.length === 0 ? (
                                <div className="psp-placeholder">
                                    <i className="fas fa-briefcase fa-3x psp-placeholder__icon" />
                                    <h3 className="psp-placeholder__title">Nenhum processo seletivo encontrado</h3>
                                    <p className="psp-placeholder__text">
                                        {searchTerm ? 'Tente alterar os termos da busca.' : 'Não há processos seletivos disponíveis no momento.'}
                                    </p>
                                </div>
                            ) : (
                                <div className="psp-process-grid">
                                    {filteredProcesses.map((process) => {
                                        const statusInfo = getProcessStatusInfo(process);
                                        const subtitle = process.units_and_staffing || (process.edital_number ? `Edital ${process.edital_number}` : '');
                                        const projectsList = process.projects || [];

                                        return (
                                            <div
                                                key={process.id}
                                                className={`psp-process-card ${statusInfo.type === 'closed' ? 'is-closed' : ''} ${statusInfo.type === 'upcoming' ? 'is-upcoming' : ''}`}
                                                onClick={() => handleProcessCardClick(process)}
                                                title={`Clique para ver as vagas de ${process.title}`}
                                            >
                                                {/* Header Visual com Gradiente e Pattern */}
                                                <div className={`psp-card-banner ${statusInfo.bannerClass}`}>
                                                    <svg className="psp-card-pattern" xmlns="http://www.w3.org/2000/svg">
                                                        <defs>
                                                            <pattern id={`pat-${process.id}`} width="20" height="20" patternUnits="userSpaceOnUse">
                                                                <path d="M0 20L20 0H10L0 10V20z" fill="#ffffff" />
                                                            </pattern>
                                                        </defs>
                                                        <rect width="100%" height="100%" fill={`url(#pat-${process.id})`} />
                                                    </svg>

                                                    <div className="psp-card-banner__badge">
                                                        <span className={`psp-status-badge ${statusInfo.badgeClass}`}>
                                                            {statusInfo.label}
                                                        </span>
                                                    </div>
                                                </div>

                                                {/* Corpo do Card */}
                                                <div className="psp-card-body">
                                                    {/* Título do Card: Título do Processo Seletivo */}
                                                    <h3 className="psp-card-title">{process.title}</h3>
                                                    {subtitle && <p className="psp-card-desc">{subtitle}</p>}

                                                    {/* Locais (Projetos) Vinculados */}
                                                    <div className="psp-card-locations">
                                                        {projectsList.length > 1 ? (
                                                            <button
                                                                type="button"
                                                                className="psp-btn-locations-link"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    openLocationsModal(`Locais do Processo: ${process.title}`, projectsList);
                                                                }}
                                                                title="Ver locais vinculados"
                                                            >
                                                                <i className="fas fa-map-marker-alt psp-icon--indigo" />
                                                                Ver locais vinculados ({projectsList.length})
                                                            </button>
                                                        ) : projectsList.length === 1 ? (
                                                            <span className="psp-hero-tag psp-hero-tag--sm">
                                                                <i className="fas fa-map-marker-alt psp-icon--mr-4 psp-icon--indigo" />
                                                                {projectsList[0].name}
                                                            </span>
                                                        ) : (
                                                            <span className="psp-card-locations__fallback">
                                                                <i className="fas fa-map-marker-alt psp-icon--mr-4" />
                                                                {process.units_and_staffing || 'Conforme edital'}
                                                            </span>
                                                        )}
                                                    </div>

                                                    {process.vacancies_count > 0 && (
                                                        <span className="psp-card-vacancies-count">
                                                            <i className="fas fa-user-tie" aria-hidden="true" />
                                                            {process.vacancies_count} {process.vacancies_count === 1 ? 'vaga disponível' : 'vagas disponíveis'}
                                                        </span>
                                                    )}

                                                    {statusInfo.isUpcomingBox ? (
                                                        <div className="psp-upcoming-alert-box">
                                                            {statusInfo.footerText}
                                                        </div>
                                                    ) : (
                                                        <div className="psp-card-footer">
                                                            <span>{statusInfo.footerText}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}

                    {/* VIEW 2: VAGAS POR CARGO E ÁREA (LISTA) */}
                    {activeTab === 'cargo' && (
                        <div className="psp-fade-in psp-job-list-view">
                            <div className="psp-list-header">
                                <h2 className="psp-list-header__title">
                                    Vagas disponíveis
                                </h2>
                                <span className="psp-list-header__count">
                                    {filteredVacancies.length} {filteredVacancies.length === 1 ? 'vaga encontrada' : 'vagas encontradas'}
                                </span>
                            </div>

                            {/* Tag de Filtro de Processo Ativo */}
                            {filters.process_id && (
                                <div className="psp-filter-chip">
                                    <span>
                                        <i className="fas fa-filter psp-icon--mr-6" />
                                        Filtrando pelo processo seletivo: <strong>{selectedProcessTitle}</strong>
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => setFilters(prev => ({ ...prev, process_id: '' }))}
                                        className="psp-filter-chip__remove"
                                        title="Remover filtro de processo"
                                    >
                                        <i className="fas fa-times" />
                                    </button>
                                </div>
                            )}

                            {/* FILTROS */}
                            <div className="psp-filters-bar">
                                <div className="psp-filter-control">
                                    <label className="psp-filter-label">Buscar</label>
                                    <input
                                        type="text"
                                        placeholder="Pesquise uma vaga"
                                        value={filters.search}
                                        onChange={(e) => handleFilterChange('search', e.target.value)}
                                        className="psp-filter-input"
                                    />
                                </div>

                                <div className="psp-filter-control">
                                    <label className="psp-filter-label">Cargo</label>
                                    <select
                                        value={filters.role_id}
                                        onChange={(e) => handleFilterChange('role_id', e.target.value)}
                                        className="psp-filter-select"
                                    >
                                        <option value="">Todos</option>
                                        {filterOptions.roles.map((role) => (
                                            <option key={role.id} value={role.id}>
                                                {role.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="psp-filter-control">
                                    <label className="psp-filter-label">Área</label>
                                    <select
                                        value={filters.area_id}
                                        onChange={(e) => handleFilterChange('area_id', e.target.value)}
                                        className="psp-filter-select"
                                    >
                                        <option value="">Todas</option>
                                        {filterOptions.areas.map((area) => (
                                            <option key={area.id} value={area.id}>
                                                {area.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="psp-filter-control">
                                    <label className="psp-filter-label">Local</label>
                                    <select
                                        value={filters.project_id}
                                        onChange={(e) => handleFilterChange('project_id', e.target.value)}
                                        className="psp-filter-select"
                                    >
                                        <option value="">Todos</option>
                                        {filterOptions.locations.map((loc) => (
                                            <option key={loc.id} value={loc.id}>
                                                {loc.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {hasActiveFilters && (
                                    <div className="psp-filter-control">
                                        <button
                                            type="button"
                                            onClick={handleClearFilters}
                                            className="psp-clear-btn"
                                            title="Limpar todos os filtros"
                                        >
                                            <i className="fas fa-times psp-icon--mr-6" />
                                            Limpar
                                        </button>
                                    </div>
                                )}
                            </div>

                            {/* LISTA DE VAGAS */}
                            {filteredVacancies.length === 0 ? (
                                <div className="psp-placeholder psp-placeholder--card">
                                    <i className="fas fa-search fa-3x psp-placeholder__icon" />
                                    <h3 className="psp-placeholder__title">Nenhuma vaga encontrada</h3>
                                    <p className="psp-placeholder__text psp-placeholder__text--spaced">
                                        Nenhuma vaga corresponde aos critérios de busca selecionados.
                                    </p>
                                    {hasActiveFilters && (
                                        <button type="button" onClick={handleClearFilters} className="psp-btn psp-btn--outline psp-btn--inline">
                                            Limpar filtros
                                        </button>
                                    )}
                                </div>
                            ) : (
                                <div className="psp-job-table-container">
                                    {/* Cabeçalho Fixo da Tabela */}
                                    <div className="psp-job-table-header">
                                        <div>CARGO / VAGA</div>
                                        <div>ÁREA</div>
                                        <div>LOCAL</div>
                                        <div className="psp-text-right">STATUS</div>
                                    </div>

                                    {filteredVacancies.map((vacancy) => {
                                        const vacStatus = getPspStatusInfo(vacancy);
                                        const vacancyProjects = vacancy.projects || [];

                                        return (
                                            <div
                                                key={vacancy.id}
                                                className="psp-job-table-row"
                                                onClick={() => navigate(`${base}/vagas/${vacancy.id}`)}
                                                title={`Ver detalhes de ${vacancy.title}`}
                                            >
                                                {/* Coluna 1: Cargo / Título */}
                                                <div className="psp-job-title-col">
                                                    <span>{vacancy.title}</span>
                                                    {vacancy.has_exam && (
                                                        <span className="psp-badge-exam" title="Vaga com prova">
                                                            Prova
                                                        </span>
                                                    )}
                                                </div>

                                                {/* Coluna 2: Área */}
                                                <div className="psp-job-meta-col">
                                                    <i className="fas fa-layer-group psp-icon--mr-6 psp-icon--muted psp-icon--sm" />
                                                    <span>{vacancy.area_role_name || 'Geral'}</span>
                                                </div>

                                                {/* Coluna 3: Local (com modal de múltiplos projetos) */}
                                                <div className="psp-job-meta-col">
                                                    {vacancyProjects.length > 1 ? (
                                                        <button
                                                            type="button"
                                                            className="psp-btn-locations-link"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                openLocationsModal(`Locais da Vaga: ${vacancy.title}`, vacancyProjects);
                                                            }}
                                                            title="Clique para ver todos os locais vinculados a esta vaga"
                                                        >
                                                            <i className="fas fa-map-marker-alt psp-icon--indigo" />
                                                            <span>{vacancyProjects.length} locais vinculados</span>
                                                        </button>
                                                    ) : vacancyProjects.length === 1 ? (
                                                        <div className="psp-job-location">
                                                            <i className="fas fa-map-marker-alt psp-icon--muted psp-icon--sm" />
                                                            <span>{vacancyProjects[0].name}</span>
                                                        </div>
                                                    ) : (
                                                        <div className="psp-job-location">
                                                            <i className="fas fa-map-marker-alt psp-icon--muted psp-icon--sm" />
                                                            <span>{vacancy.units_and_staffing || 'Geral'}</span>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Coluna 4: Ação / Status (Largura Fixa para não desalinhar) */}
                                                <div className="psp-job-action-col">
                                                    <span className={`psp-job-status ${vacStatus.textClass}`}>
                                                        {vacStatus.tableLabel}
                                                    </span>
                                                    <i className="fas fa-chevron-right psp-icon--muted psp-icon--xs" />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}

            {/* MODAL DE LOCAIS (PROJETOS) VINCULADOS */}
            {locationsModal.open && (
                <div className="psp-modal-backdrop" onClick={closeLocationsModal}>
                    <div className="psp-modal psp-modal--md" onClick={(e) => e.stopPropagation()}>
                        <div className="psp-modal__header">
                            <h2>{locationsModal.title}</h2>
                        </div>
                        <div className="psp-modal__body">
                            <p className="psp-modal__intro">
                                Esta oportunidade abrange os seguintes projetos / unidades vinculadas:
                            </p>
                            <div className="psp-item-list">
                                {locationsModal.projects.map((proj) => (
                                    <div
                                        key={proj.id}
                                        className="psp-list-item psp-list-item--gap"
                                    >
                                        <div className="psp-list-item__main psp-list-item__main--wide">
                                            <div className="psp-list-item__badge">
                                                <i className="fas fa-building" />
                                            </div>
                                            <div>
                                                <strong className="psp-list-item__name">
                                                    {proj.name}
                                                </strong>
                                            </div>
                                        </div>

                                        <button
                                            type="button"
                                            className="psp-btn psp-btn--outline psp-btn--inline psp-btn--xs"
                                            onClick={() => {
                                                closeLocationsModal();
                                                setFilters(prev => ({ ...prev, project_id: proj.id }));
                                                setActiveTab('cargo');
                                            }}
                                        >
                                            Filtrar por este local
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="psp-modal__footer">
                            <button type="button" className="psp-btn psp-btn--outline" onClick={closeLocationsModal}>
                                Fechar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default JobList;
